import './insured-policies.detail.scss';

import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CreateClaimModal,
  InsuredEntityProps,
  PolicyDetailProps,
} from './overview';

import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import {
  NewDisplayFormatDate,
  capitalizeLetterRemoveUnderLine,
} from '@converge/utils';
import {
  NotesListDetailData,
  TransactionStatus,
  TransactionType,
  getFirstPaginatedNode,
  useHasOpenClaimsQuery,
} from '@converge/graphql';
import React, { useCallback, useState } from 'react';

import { InsuredSubmissionsTable } from './insured-submissions.table';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { InsuredEntityPageContainer } from './insured-entity.page.container';
import { MidtermDocumentModal } from './midterm-transaction/midterm-document-modal';
import { MidtermTransactionModalCancellation } from './midterm-transaction/midterm-transaction-modal-cancellation';
import { MidtermTransactionModalForWaiverChangeControl } from './midterm-transaction/midterm-transaction-modal-waiver-change-control';
import { MidtermTransactionModalReinstatement } from './midterm-transaction/midterm-transaction-modal-reinstatement';
import { MidtermTransactionNotesModal } from './midterm-transaction/midterm-transaction-notes-modal';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { formatDollarAmount } from '@converge/utils';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ConvergeNavigationUrls } from '../../../contexts/use-converge-navigation';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
// Define the enum locally to avoid dependency on non-buildable library
enum ConvergePdfTemplateType {
  ExtendedReportingEndorsement = 'OPTIONAL_EXTENDED_REPORTING_ENDORSEMENT',
  CancellationEndorsement = 'CANCELLATION_ENDORSEMENT',
  WaiverEndorsement = 'WAIVER_ENDORSEMENT',
  PolicyReinstatementEndorsement = 'REINSTATEMENT_ENDORSEMENT',
  ChangeBrokerEndorsement = 'CHANGE_BROKER_ENDORSEMENT',
  PolicyExtensionEndorsement = 'POLICY_EXTENSION_ENDORSEMENT',
}

export enum ModalStep {
  REINSTATEMENT_DETAILS = 'REINSTATEMENT_DETAILS',
  ACTIVE_CLAIM_INFO = 'ACTIVE_CLAIM_INFO',
  INACTIVE_INFO = 'INACTIVE_INFO',
  CANCELLATION_DETAILS = 'CANCELLATION_DETAILS',
  TRANSACTION_NOTE = 'TRANSACTION_NOTE',
}

export enum env {
  Dev = 'development',
  Staging = 'staging',
  local = 'dev',
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} sx={{ flexDirection: 'row' }} />
))(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingLeft: '12px',
}));

export type PolicyAction = {
  label: string;
  onClick?: (uuid: string) => void;
  hidden?: boolean;
};

export const PolicyTooltip = (actions: PolicyAction[], policyUuid: string) => (
  <Tooltip
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'white',
          color: 'black',
          boxShadow: 1,
          border: '1px solid rgba(0,0,0,0.12)',
          maxWidth: 'none',
          p: 0,
        },
      },
    }}
    title={
      <Box
        sx={{
          backgroundColor: 'white',
          color: 'black',
          margin: 0,
          padding: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            px: 2,
            py: 1,
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '12px',
            color: '#666',
          }}
        >
          Select Action
        </Typography>
        <Divider />
        <List dense disablePadding sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {actions.map(
            (action, idx) =>
              !action.hidden && (
                <ListItemButton
                  key={idx}
                  onClick={() =>
                    action.onClick ? action.onClick(policyUuid) : null
                  }
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#CEFCF6',
                      color: 'black',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#CEFCF6',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: '#CEFCF6',
                      },
                    },
                  }}
                >
                  <ListItemText primary={action.label} />
                </ListItemButton>
              )
          )}
        </List>
      </Box>
    }
  >
    <ModeOutlinedIcon
      data-testid="policy-actions-button"
      style={{
        cursor: 'pointer',
        backgroundColor: '#08F0D1',
        padding: '5px',
        marginTop: '20px',
        marginRight: '20px',
        borderRadius: '3px',
      }}
    />
  </Tooltip>
);
export const InsuredPoliciesDetails = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isActivePoliciesOpen, setActivePoliciesOpen] = useState(true);
  const [isExpiredPoliciesOpen, setExpiredPoliciesOpen] = useState(true);
  const [policySelected, setPolicySelected] = useState<PolicyDetailProps>();
  const [effectiveDate, setEffectiveDate] = useState<string>();
  const [maxDate, setMaxDate] = useState<Date>();
  const [openModal, isOpenModal] = useState(false);
  const [selectedMidtermUuid, setSelectedMidtermUuid] = useState<string>();
  const [selectedMidtermType, setSelectedMidtermType] = useState<string>();
  const setMaxDateHandler = (date?: string) => {
    setMaxDate(date ? moment(date).utc().toDate() : undefined);
  };
  const [transactionTypeSelected, setTransactionTypeSelected] =
    useState<TransactionType>();
  const [selectedNote, setSelectedNote] = useState<NotesListDetailData>();

  const navigate = useNavigate();
  const { insuredUuid } = useParams<{ insuredUuid: string }>();

  const handleClose = useCallback(() => {
    setTransactionTypeSelected(undefined);
    setPolicySelected(undefined);
  }, []);

  const { insuredEntity, policiesList } =
    useOutletContext<InsuredEntityProps>();

  const handleChangeActivePoliciesAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setActivePoliciesOpen(newExpanded);
  };

  const handleChangeExpiredPoliciesAccordion = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setExpiredPoliciesOpen(newExpanded);
  };

  const renderTableCell = (value?: string | number, label?: string) => (
    <div>
      <Typography className="policies-table-cell-label">{label}</Typography>
      <Typography variant="body2" className="policies-table-cell">
        {value ?? '—'}
      </Typography>
    </div>
  );
  const checkTypeForDoc = (type: string) => {
    if (type === TransactionType.Cancellation) {
      return ConvergePdfTemplateType.CancellationEndorsement;
    } else if (type === TransactionType.WaiverOfChangeOfControl) {
      return ConvergePdfTemplateType.WaiverEndorsement;
    } else if (type === TransactionType.Reinstatement) {
      return ConvergePdfTemplateType.PolicyReinstatementEndorsement;
    } else if (type === TransactionType.ChangeBroker) {
      return ConvergePdfTemplateType.ChangeBrokerEndorsement;
    } else if (type === TransactionType.ExtendedReportingPeriod) {
      return ConvergePdfTemplateType.ExtendedReportingEndorsement;
    } else if (type === TransactionType.PolicyExtension) {
      return ConvergePdfTemplateType.PolicyExtensionEndorsement;
    } else {
      return ConvergePdfTemplateType.ExtendedReportingEndorsement;
    }
  };
  const findMttFromUuid = (mttUuid: string) => {
    const activePolicyMttFromUuid = policiesList.activePolicies
      .flatMap((policy) => policy.midtermTransactions.edges)
      .find((edge) => edge.node.uuid === mttUuid)?.node;

    const expiredPolicyMttFromUuid = policiesList.expiredPolicies
      .flatMap((policy) => policy.midtermTransactions.edges)
      .find((edge) => edge.node.uuid === mttUuid)?.node;

    return activePolicyMttFromUuid || expiredPolicyMttFromUuid;
  };

  const setPolicySelectedForModal = (policy?: PolicyDetailProps) => {
    if (policy) {
      setPolicySelected(policy || undefined);
      setEffectiveDate(
        policy?.startDate ? moment(policy.startDate).utc().format() : undefined
      );
      setMaxDateHandler(policy.endDate);
    }
  };
  const actionsInactivePolicy: PolicyAction[] = [
    {
      label: 'Reinstate this policy',
      onClick: (uuid) => {
        const policy = policiesList.expiredPolicies.find(
          (p) => p.uuid === uuid
        );
        setTransactionTypeSelected(TransactionType.Reinstatement);
        setPolicySelectedForModal(policy);
      },
      hidden: false,
    },
  ];

  const hasActiveMTT = (type: TransactionType, policy?: PolicyDetailProps) => {
    const edge = policy?.midtermTransactions.edges.find(
      (e) => e.node.type === type
    );
    if (!edge) return false;
    return type === TransactionType.Cancellation
      ? edge.node.status !== TransactionStatus.Canceled
      : edge.node.status === TransactionStatus.InProgress;
  };

  const renderPolicyCard = (
    policy: PolicyDetailProps,
    isActive: boolean,
    policyIndex: number
  ) => {
    const hasActiveCancellation = hasActiveMTT(
      TransactionType.Cancellation,
      policy
    );
    const hasActiveReinstatement = hasActiveMTT(
      TransactionType.Reinstatement,
      policy
    );
    const hasErp =
      policy?.midtermTransactions?.edges?.some(
        (e) =>
          e.node.type === TransactionType.ExtendedReportingPeriod &&
          e.node.status !== TransactionStatus.Canceled
      ) ?? false;
    const hasActivePolicy = policiesList.activePolicies.length > 0;
    const inactiveActions =
      !hasActiveReinstatement && !hasActivePolicy ? actionsInactivePolicy : [];
    const showActiveIcon = isActive && !hasActiveCancellation;
    const showInactiveIcon = !isActive && inactiveActions.length > 0;

    const idx = policyIndex + 1;

    return (
      <Box
        key={policy.uuid}
        data-testid={`policy-card_${idx}`}
        className={`ipd-policy-card ${
          !isActive ? 'ipd-policy-card--inactive' : ''
        }`}
      >
        {(showActiveIcon || showInactiveIcon) && (
          <Box className="ipd-policy-card__actions">
            {showActiveIcon ? (
              <Tooltip
                title={
                  hasErp
                    ? 'The ERP on this policy prevents any additional transactions'
                    : 'Start a midterm transaction'
                }
                arrow
                placement="top"
                leaveDelay={300}
                componentsProps={{
                  tooltip: {
                    sx: {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#333333',
                      borderRadius: '3px',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '500',
                      padding: '10px 16px 12px',
                      margin: '0 auto',
                    },
                  },
                  arrow: { sx: { color: '#333333' } },
                }}
              >
                <ModeOutlinedIcon
                  data-testid="policy-actions-button"
                  style={{
                    cursor: hasErp ? 'not-allowed' : 'pointer',
                    backgroundColor: '#08F0D1',
                    padding: '5px',
                    marginTop: '20px',
                    marginRight: '20px',
                    borderRadius: '3px',
                    opacity: hasErp ? 0.5 : 1,
                  }}
                  onClick={() => {
                    if (hasErp) return;
                    navigate(
                      ConvergeNavigationUrls.midtermTransactionWizard({
                        insuredEntityUuid: insuredUuid ?? '',
                        policyUuid: policy.uuid ?? '',
                      })
                    );
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip
                title={inactiveActions[0].label}
                arrow
                placement="top"
                leaveDelay={300}
                componentsProps={{
                  tooltip: {
                    sx: {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#333333',
                      borderRadius: '3px',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '500',
                      padding: '10px 16px 12px',
                      margin: '0 auto',
                    },
                  },
                  arrow: { sx: { color: '#333333' } },
                }}
              >
                <ModeOutlinedIcon
                  data-testid="policy-actions-button"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: '#08F0D1',
                    padding: '5px',
                    marginTop: '20px',
                    marginRight: '20px',
                  }}
                  onClick={() =>
                    inactiveActions[0].onClick?.(policy.uuid ?? '')
                  }
                />
              </Tooltip>
            )}
          </Box>
        )}
        <Box className="ipd-policy-card__row">
          <Box className="ipd-policy-card__cell ipd-policy-card__cell--first">
            <div data-testid={`policy-number_${idx}`}>
              <div>
                <Typography className="policies-table-cell-label">
                  POLICY NUMBER
                </Typography>
                <Typography
                  variant="body2"
                  className="policies-table-cell"
                  onClick={
                    policy.documentLink
                      ? () =>
                          window.open(
                            policy.documentLink ?? undefined,
                            '_blank'
                          )
                      : undefined
                  }
                  sx={
                    policy.documentLink
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : undefined
                  }
                >
                  {policy.referenceId ?? '—'}
                </Typography>
              </div>
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-type_${idx}`}>
              {renderTableCell(policy.type ?? '—', 'POLICY TYPE')}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-effective-date_${idx}`}>
              {renderTableCell(
                policy.startDate ? NewDisplayFormatDate(policy.startDate) : '—',
                'EFFECTIVE DATE'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-expiration-date_${idx}`}>
              {renderTableCell(
                policy.endDate ? NewDisplayFormatDate(policy.endDate) : '—',
                'EXPIRATION DATE'
              )}
            </div>
          </Box>
        </Box>
        <Box className="ipd-policy-card__row second-policy-card-row">
          <Box className="ipd-policy-card__cell ipd-policy-card__cell--first">
            <div data-testid={`policy-broker_${idx}`}>
              <div>
                <Typography className="policies-table-cell-label">
                  BROKER
                </Typography>
                <Typography
                  variant="body2"
                  className="policies-table-cell"
                  onClick={
                    policy.brokerUuid
                      ? () =>
                          window.open(
                            ConvergeNavigationUrls.broker({
                              brokerUuid: policy.brokerUuid ?? undefined,
                            }),
                            '_blank'
                          )
                      : undefined
                  }
                  sx={
                    policy.brokerUuid
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : undefined
                  }
                >
                  {policy.brokerName ?? '—'}
                </Typography>
              </div>
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-premium_${idx}`}>
              {renderTableCell(
                policy.grossPremium
                  ? formatDollarAmount(policy.grossPremium)
                  : '—',
                'PREMIUM'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-limit_${idx}`}>
              {renderTableCell(
                policy.limit ? formatDollarAmount(policy.limit) : '—',
                'LIMIT'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`policy-retention_${idx}`}>
              {renderTableCell(
                policy.retention ? formatDollarAmount(policy.retention) : '—',
                'RETENTION'
              )}
            </div>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderMidtermCard = (
    edge: {
      node: {
        uuid: string;
        type: TransactionType;
        status: string;
        createdBy?: string | null;
        effectiveDate?: string | null;
        refundAmount?: string | null;
        additionalPremium?: string | null;
        createdAt?: string | null;
        mttPolicyExtensionDate?: string | null;
        mttErpYears?: string | null;
      };
    },
    isActive = true,
    isLatestCancellation = true,
    mttIndex = 0,
    policyEndDate?: string | null
  ) => {
    const { node } = edge;
    const mtt = findMttFromUuid(node.uuid);
    const currentDate = moment().utc();
    const isPending = node.effectiveDate
      ? moment(node.effectiveDate).isAfter(currentDate)
      : false;
    const isCancellation = node.type === TransactionType.Cancellation;
    const isERP = node.type === TransactionType.ExtendedReportingPeriod;
    const isPolicyExtension = node.type === TransactionType.PolicyExtension;
    const extendedExpirationDate =
      isPolicyExtension && node.mttPolicyExtensionDate
        ? NewDisplayFormatDate(node.mttPolicyExtensionDate)
        : isERP && policyEndDate && node.mttErpYears
        ? moment(policyEndDate)
            .add(Number(node.mttErpYears), 'years')
            .format('MM/DD/YYYY')
        : null;
    const isCanceled = node.status === TransactionStatus.Canceled;
    const premiumValue = isCancellation
      ? mtt?.refundAmount
      : isERP || isPolicyExtension
      ? node.additionalPremium ?? mtt?.additionalPremium
      : mtt?.additionalPremium;
    const premiumDisplay =
      premiumValue != null
        ? formatDollarAmount(
            parseFloat(String(premiumValue)),
            false,
            isCancellation
          )
        : 'N/A';

    let completedOnDisplay = '—';
    if (
      node.status === TransactionStatus.Completed &&
      mtt?.effectiveDate &&
      !isPending
    ) {
      completedOnDisplay = NewDisplayFormatDate(mtt.effectiveDate);
    }

    const effectiveDateDisplay = mtt?.effectiveDate
      ? NewDisplayFormatDate(mtt.effectiveDate)
      : '—';

    const hasNote = node.type !== TransactionType.Reinstatement;
    const idx = mttIndex + 1;

    // Active policy: all canceled cancellations show CANCELED in COMPLETED ON
    // Inactive policy: only non-latest canceled cancellations show CANCELED in COMPLETED ON
    const showCanceledChip =
      isCancellation && isCanceled && (isActive || !isLatestCancellation);

    const renderCompletedOnCell = () => {
      if (showCanceledChip) {
        return (
          <div>
            <Typography className="policies-table-cell-label">
              COMPLETED ON
            </Typography>
            <Chip
              data-testid={`mtt-completed-on-canceled_${idx}`}
              label="CANCELED"
              size="small"
              sx={{
                backgroundColor: '#D32F2F',
                color: 'white',
                mt: '4px',
                paddingLeft: '8px',
                paddingRight: '8px',
                letterSpacing: '0.5px',
                fontWeight: '500',
                fontSize: '12px',
              }}
            />
          </div>
        );
      }
      return (
        <div data-testid={`mtt-completed-on_${idx}`}>
          {renderTableCell(completedOnDisplay, 'COMPLETED ON')}
        </div>
      );
    };

    const renderEffectiveDateCell = () => {
      if (showCanceledChip) {
        return (
          <div data-testid={`mtt-effective-date_${idx}`}>
            {renderTableCell('—', 'EFFECTIVE DATE')}
          </div>
        );
      }
      if (isPending) {
        return (
          <div>
            <Typography className="policies-table-cell-label">
              EFFECTIVE DATE
            </Typography>
            <Chip
              data-testid={`mtt-effective-date-pending_${idx}`}
              label="PENDING"
              size="small"
              sx={{
                backgroundColor: '#0288D1',
                color: 'white',
                mt: '4px',
                paddingLeft: '8px',
                paddingRight: '8px',
                letterSpacing: '0.5px',
                fontWeight: '500',
                fontSize: '12px',
              }}
            />
          </div>
        );
      }
      return (
        <div data-testid={`mtt-effective-date_${idx}`}>
          {renderTableCell(effectiveDateDisplay, 'EFFECTIVE DATE')}
        </div>
      );
    };

    return (
      <Box
        key={node.uuid}
        data-testid={`mtt-card_${idx}`}
        className={`ipd-mtt-card${!isActive ? ' ipd-mtt-card--inactive' : ''}`}
      >
        <Box className="icons_mtt-container">
          <FolderOpenOutlinedIcon
            data-testid={`view-endorsement-button_${idx}`}
            className="icon_mtt"
            onClick={() => {
              setSelectedMidtermUuid(node.uuid);
              setSelectedMidtermType(checkTypeForDoc(node.type || ''));
              isOpenModal(true);
            }}
          />
          {hasNote && (
            <InsertCommentOutlinedIcon
              data-testid={`view-note-button_${idx}`}
              className="icon_mtt"
              onClick={() => {
                const note = getFirstPaginatedNode(mtt?.notes);
                setSelectedNote(note);
              }}
            />
          )}
        </Box>
        {/* Row 1: Transaction Type, Initiated On, Completed On */}
        <Box className="ipd-policy-card__row">
          <Box className="ipd-policy-card__cell ipd-policy-card__cell--first">
            <div data-testid={`mtt-type_${idx}`}>
              {renderTableCell(
                node.type
                  ? node.type === TransactionType.ExtendedReportingPeriod
                    ? 'Extended Reporting Period (ERP)'
                    : capitalizeLetterRemoveUnderLine(node.type)
                  : '—',
                'MIDTERM TRANSACTION'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`mtt-initiated-on_${idx}`}>
              {renderTableCell(
                mtt?.createdAt ? NewDisplayFormatDate(mtt.createdAt) : '—',
                'INITIATED ON'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">{renderCompletedOnCell()}</Box>
          <Box className="ipd-policy-card__cell">
            {extendedExpirationDate != null &&
              renderTableCell(
                extendedExpirationDate,
                'EXTENDED EXPIRATION DATE'
              )}
          </Box>
        </Box>
        {/* Row 2: Initiated By, Additional Premium / Refund, Effective Date */}
        <Box className="ipd-policy-card__row">
          <Box className="ipd-policy-card__cell ipd-policy-card__cell--first">
            <div data-testid={`mtt-initiated-by_${idx}`}>
              {renderTableCell(node.createdBy ?? '—', 'INITIATED BY')}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            <div data-testid={`mtt-premium_${idx}`}>
              {renderTableCell(
                premiumDisplay,
                isCancellation ? 'REFUND AMOUNT' : 'ADDITIONAL PREMIUM'
              )}
            </div>
          </Box>
          <Box className="ipd-policy-card__cell">
            {renderEffectiveDateCell()}
          </Box>
          <Box className="ipd-policy-card__cell" />
        </Box>
      </Box>
    );
  };

  const { loading: claimsLoading, hasOpenClaims } = useHasOpenClaimsQuery({
    policyUuid: policySelected?.uuid || '',
    enabled: Boolean(policySelected?.uuid),
  });

  const PoliciesAndMidtermTransactions = (
    title: string,
    noData: string,
    policies: PolicyDetailProps[],
    isOpen: boolean,
    handleChangePoliciesAccordion: (
      event: React.SyntheticEvent,
      newExpanded: boolean
    ) => void,
    policySelected?: PolicyDetailProps,
    transactionTypeSelected?: TransactionType
  ) => {
    const sectionId = title === 'ACTIVE POLICIES' ? 'active' : 'inactive';
    return (
      <div className="policy-accordion-wrapper">
        <Accordion expanded={isOpen} onChange={handleChangePoliciesAccordion}>
          <AccordionSummary
            data-testid={`${sectionId}-policies-accordion`}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isOpen ? (
                <KeyboardArrowDownIcon sx={{ marginTop: '10px' }} />
              ) : (
                <KeyboardArrowRightIcon sx={{ marginTop: '10px' }} />
              )}
              <Box className="policy-card-title">{title}</Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="accordion-details">
            {policies?.length > 0 ? (
              <>
                {policies.map((policy, policyIndex) => (
                  <div
                    key={policy.uuid}
                    data-testid={`policy-group_${policyIndex + 1}`}
                    className="ipd-policy-group"
                  >
                    {renderPolicyCard(
                      policy,
                      title === 'ACTIVE POLICIES',
                      policyIndex
                    )}
                    {policy.midtermTransactions.edges.length > 0 &&
                      (() => {
                        const isActivePolicies = title === 'ACTIVE POLICIES';
                        const cancellationEdges =
                          policy.midtermTransactions.edges.filter(
                            (e) => e.node.type === TransactionType.Cancellation
                          );
                        const latestCancellationUuid =
                          cancellationEdges[0]?.node.uuid;
                        return policy.midtermTransactions.edges.map(
                          (edge, mttIndex) =>
                            renderMidtermCard(
                              edge,
                              isActivePolicies,
                              edge.node.type !== TransactionType.Cancellation ||
                                edge.node.uuid === latestCancellationUuid,
                              mttIndex,
                              policy.endDate
                            )
                        );
                      })()}
                  </div>
                ))}
              </>
            ) : (
              <div
                data-testid={`${sectionId}-policies-empty-state`}
                className="empty-state-message"
              >
                {title === 'ACTIVE POLICIES'
                  ? 'There are no active policies for this insured.'
                  : 'There are no inactive policies for this insured.'}
              </div>
            )}
            {policySelected &&
              policySelected.uuid &&
              transactionTypeSelected === TransactionType.Cancellation && (
                <MidtermTransactionModalCancellation
                  transactionType={transactionTypeSelected}
                  policyUuid={policySelected.uuid}
                  onClose={handleClose}
                  effectiveDate={effectiveDate}
                  maxDate={maxDate}
                  showClaimMessage={hasOpenClaims}
                  hasOpenClaims={hasOpenClaims}
                  claimsLoading={claimsLoading}
                />
              )}
            {policySelected &&
              policySelected.uuid &&
              transactionTypeSelected ===
                TransactionType.WaiverOfChangeOfControl && (
                <MidtermTransactionModalForWaiverChangeControl
                  transactionType={transactionTypeSelected}
                  policyUuid={policySelected.uuid}
                  onClose={handleClose}
                  effectiveDate={effectiveDate}
                  maxDate={maxDate}
                  showClaimMessage={hasOpenClaims}
                  hasOpenClaims={hasOpenClaims}
                  claimsLoading={claimsLoading}
                />
              )}

            {policySelected &&
              policySelected.uuid &&
              transactionTypeSelected === TransactionType.Reinstatement && (
                <MidtermTransactionModalReinstatement
                  transactionType={transactionTypeSelected}
                  policySelected={policySelected}
                  onClose={handleClose}
                  showInactive={policySelected.showCancellationModal}
                  showClaimMessage={hasOpenClaims}
                  hasOpenClaims={hasOpenClaims}
                  claimsLoading={claimsLoading}
                />
              )}
            {selectedNote && (
              <MidtermTransactionNotesModal
                note={selectedNote}
                onClose={() => setSelectedNote(undefined)}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  return (
    <>
      <InsuredEntityPageContainer
        title="Policy Information"
        // onNext={() => navigateToQuotes()}
      >
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={(_e, newValue: number) => setTabValue(newValue)}
            aria-label="policy tabs"
            sx={{
              color: '#000000 !important',
              borderBottom: '1px solid #000000',
              margin: '0 52px',
              '& .MuiTab-root': {
                fontSize: '16px',
                fontWeight: 400,
                color: '#000000',
                textTransform: 'none',
              },
              '& .Mui-selected': {
                fontWeight: 700,
                color: '#000000 !important',
              },
            }}
            variant="fullWidth"
          >
            <Tab
              data-testid="policies-tab"
              label="Policies"
              id="policy-tab-0"
              aria-controls="policy-tabpanel-0"
            />
            <Tab
              data-testid="submissions-tab"
              label="Submissions"
              id="policy-tab-1"
              aria-controls="policy-tabpanel-1"
            />
          </Tabs>

          <div
            role="tabpanel"
            hidden={tabValue !== 0}
            id="policy-tabpanel-0"
            aria-labelledby="policy-tab-0"
          >
            {tabValue === 0 && (
              <Box sx={{ p: 3 }}>
                {PoliciesAndMidtermTransactions(
                  'ACTIVE POLICIES',
                  'There are no active policies for this insured.',
                  policiesList.activePolicies,
                  isActivePoliciesOpen,
                  handleChangeActivePoliciesAccordion,
                  policySelected,
                  transactionTypeSelected
                )}
                {PoliciesAndMidtermTransactions(
                  'INACTIVE POLICIES',
                  'There are no inactive policies for this insured.',
                  policiesList.expiredPolicies,
                  isExpiredPoliciesOpen,
                  handleChangeExpiredPoliciesAccordion
                )}
              </Box>
            )}
          </div>

          <div
            role="tabpanel"
            hidden={tabValue !== 1}
            id="policy-tabpanel-1"
            aria-labelledby="policy-tab-1"
          >
            {tabValue === 1 && (
              <Box sx={{ p: 3 }}>
                <InsuredSubmissionsTable
                  entityUuid={insuredEntity.uuid ?? ''}
                />
              </Box>
            )}
          </div>
        </Box>
      </InsuredEntityPageContainer>
      {showCreateForm && (
        <CreateClaimModal
          onClose={() => setShowCreateForm(false)}
          title="Create claim"
          insuredUuid={insuredEntity.uuid}
        />
      )}
      {openModal && selectedMidtermUuid && selectedMidtermType && (
        <MidtermDocumentModal
          isOpen={openModal}
          onClose={() => isOpenModal(false)}
          midtermUuid={selectedMidtermUuid}
          midtermType={selectedMidtermType}
        />
      )}
    </>
  );
};
